/**
 * SellerTab — owner / decision-maker contact card.
 *
 * Post-ReSimpli-unification, the seller info still lives on the legacy
 * `leads` collection (fields: seller_name, seller_phone, seller_email).
 * Since `property.id === lead.id` for every unified record, we fetch the
 * lead by the property id, render an inline-editable card, and PATCH
 * back to /api/leads/{id}. The lead-property sync then mirrors the
 * update onto v3.properties automatically.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, User, Phone, Mail, Pencil, Save, X, MessageSquare, PhoneCall,
} from "lucide-react";

export default function SellerTab({ property }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ seller_name: "", seller_phone: "", seller_email: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${property.id}`);
      setLead(data);
      setForm({
        seller_name: data.seller_name || "",
        seller_phone: data.seller_phone || "",
        seller_email: data.seller_email || "",
      });
    } catch {
      // Some V3 properties may have no matching lead (manual create) —
      // that's fine, just show an empty editable card.
      setLead(null);
    } finally {
      setLoading(false);
    }
  }, [property.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${property.id}`, form);
      toast.success("Seller info saved");
      setEditing(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="seller-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading seller info…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-panel-seller">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <User className="w-3.5 h-3.5" /> Seller / Decision Maker
          </div>
          {!editing ? (
            <button
              type="button" onClick={() => setEditing(true)}
              data-testid="seller-edit"
              className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                type="button" onClick={() => { setEditing(false); load(); }}
                data-testid="seller-cancel"
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
              <button
                type="button" onClick={save} disabled={saving}
                data-testid="seller-save"
                className="text-[11px] font-semibold rounded-md bg-slate-900 text-white px-2.5 py-1 hover:bg-slate-800 inline-flex items-center gap-1"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          )}
        </div>

        <div className="p-4 space-y-3">
          <Field
            label="Name" icon={User} editing={editing} value={form.seller_name}
            display={lead?.seller_name || "—"}
            onChange={(v) => setForm((f) => ({ ...f, seller_name: v }))}
            testid="seller-name"
          />
          <Field
            label="Phone" icon={Phone} editing={editing} value={form.seller_phone}
            display={lead?.seller_phone || "—"}
            onChange={(v) => setForm((f) => ({ ...f, seller_phone: v }))}
            testid="seller-phone" type="tel"
          />
          <Field
            label="Email" icon={Mail} editing={editing} value={form.seller_email}
            display={lead?.seller_email || "—"}
            onChange={(v) => setForm((f) => ({ ...f, seller_email: v }))}
            testid="seller-email" type="email"
          />
        </div>

        {/* Quick reach buttons */}
        {!editing && lead?.seller_phone && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex flex-wrap gap-2">
            <a
              href={`tel:${lead.seller_phone}`}
              data-testid="seller-call"
              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-[11.5px] font-semibold px-2.5 py-1.5 hover:bg-slate-800"
            >
              <PhoneCall className="w-3 h-3" /> Call
            </a>
            <a
              href={`sms:${lead.seller_phone}`}
              data-testid="seller-sms"
              className="inline-flex items-center gap-1 rounded-lg bg-white border border-slate-200 text-slate-700 text-[11.5px] font-semibold px-2.5 py-1.5 hover:bg-slate-50"
            >
              <MessageSquare className="w-3 h-3" /> SMS
            </a>
            {lead?.seller_email && (
              <a
                href={`mailto:${lead.seller_email}`}
                data-testid="seller-email-link"
                className="inline-flex items-center gap-1 rounded-lg bg-white border border-slate-200 text-slate-700 text-[11.5px] font-semibold px-2.5 py-1.5 hover:bg-slate-50"
              >
                <Mail className="w-3 h-3" /> Email
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, editing, value, display, onChange, testid, type = "text" }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-0.5">
          {label}
        </div>
        {editing ? (
          <input
            type={type} value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testid}-input`}
            className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        ) : (
          <div className="text-[13.5px] font-semibold text-slate-900" data-testid={testid}>{display}</div>
        )}
      </div>
    </div>
  );
}
